import { useEffect, useState, type RefObject } from 'react'

// The stack's resting footprint is its icon-only column. Chips grow labels only
// while peeking or expanded — transient states that shouldn't reshape the grid.
const FAB_WIDTH_PX = 48
const EDGE_SLACK_PX = 4
const SWIPE_PX = 30
const SWIPE_HIDE_MS = 6000
// How much of a parked stack stays on screen, so it reads as tucked away rather than gone.
const PEEK_PX = 14

// Slides the floating stack away while it's parked in the grid's bottom-right
// corner over a set card — the one spot no amount of scrolling can pull out from
// under it. Scrolling up or left brings it back. A grid that doesn't scroll has
// no corner to scroll into, so there a rightward swipe (or wheel) stands in for
// it: hides the stack if it covers a card, and a leftward one brings it back.
// A swipe hide wears off on its own, so a stray swipe can't strand the user
// without the stack.
export function useFabClearance({
  scrollerRef,
  fabsRef,
  blockRefs,
  layoutKey,
}: {
  scrollerRef: RefObject<HTMLDivElement | null>
  fabsRef: RefObject<HTMLDivElement | null>
  blockRefs: RefObject<Map<string, HTMLDivElement>>
  layoutKey: string
}): { hideFabs: boolean; peekShift: number } {
  const [hideFabs, setHideFabs] = useState(false)
  const [peekShift, setPeekShift] = useState(0)

  useEffect(() => {
    const scroller = scrollerRef.current
    const fabs = fabsRef.current
    if (!scroller || !fabs) return
    let frame = 0
    let swipedAway = false
    let swipeTimer: ReturnType<typeof setTimeout> | undefined

    const measure = () => {
      frame = 0
      const parent = fabs.offsetParent
      if (!parent) return
      // offset* ignore the hide transform, so the stack's resting rect stays put
      // while it's slid away — no feedback loop between hiding and measuring.
      const parentRect = parent.getBoundingClientRect()
      const right = parentRect.left + fabs.offsetLeft + fabs.offsetWidth
      const left = right - FAB_WIDTH_PX
      const top = parentRect.top + fabs.offsetTop
      const bottom = top + fabs.offsetHeight
      // The scroller bleeds to the screen edge on mobile, so its right edge is
      // where a parked stack should leave its sliver. Measured from the icon
      // column, not the box — the hidden tier's labels make the box much wider.
      setPeekShift(Math.max(0, Math.round(scroller.getBoundingClientRect().right - PEEK_PX - left)))
      const atBottom = scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - EDGE_SLACK_PX
      const atRight = scroller.scrollLeft + scroller.clientWidth >= scroller.scrollWidth - EDGE_SLACK_PX
      const scrollable =
        scroller.scrollHeight - scroller.clientHeight > EDGE_SLACK_PX ||
        scroller.scrollWidth - scroller.clientWidth > EDGE_SLACK_PX
      setHideFabs(
        (scrollable ? atBottom && atRight : swipedAway) &&
          [...blockRefs.current.values()].some(el => {
            const r = el.getBoundingClientRect()
            return r.left < right && r.right > left && r.top < bottom && r.bottom > top
          }),
      )
    }

    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(measure)
    }
    const swipe = (dx: number) => {
      clearTimeout(swipeTimer)
      swipedAway = dx > 0
      if (swipedAway) swipeTimer = setTimeout(() => swipe(-1), SWIPE_HIDE_MS)
      schedule()
    }
    let touchX = 0
    let touchY = 0
    const onTouchStart = (e: TouchEvent) => {
      touchX = e.touches[0].clientX
      touchY = e.touches[0].clientY
    }
    const onTouchMove = (e: TouchEvent) => {
      const dx = touchX - e.touches[0].clientX
      const dy = touchY - e.touches[0].clientY
      if (Math.abs(dx) < SWIPE_PX || Math.abs(dx) < Math.abs(dy)) return
      swipe(dx)
      touchX = e.touches[0].clientX
      touchY = e.touches[0].clientY
    }
    const onWheel = (e: WheelEvent) => {
      if (Math.abs(e.deltaX) > EDGE_SLACK_PX && Math.abs(e.deltaX) > Math.abs(e.deltaY)) swipe(e.deltaX)
    }

    schedule()
    scroller.addEventListener('scroll', schedule, { passive: true })
    scroller.addEventListener('touchstart', onTouchStart, { passive: true })
    scroller.addEventListener('touchmove', onTouchMove, { passive: true })
    scroller.addEventListener('wheel', onWheel, { passive: true })
    const observer = new ResizeObserver(schedule)
    observer.observe(scroller)
    observer.observe(fabs)
    if (scroller.firstElementChild) observer.observe(scroller.firstElementChild)
    return () => {
      cancelAnimationFrame(frame)
      clearTimeout(swipeTimer)
      scroller.removeEventListener('scroll', schedule)
      scroller.removeEventListener('touchstart', onTouchStart)
      scroller.removeEventListener('touchmove', onTouchMove)
      scroller.removeEventListener('wheel', onWheel)
      observer.disconnect()
    }
  }, [scrollerRef, fabsRef, blockRefs, layoutKey])

  return { hideFabs, peekShift }
}
